import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Ticket,
  DollarSign,
  Users,
  Heart,
  TrendingUp,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Calendar,
  CreditCard,
  Wallet,
  UserCheck,
  Award,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart as PieChartIcon,
  Filter,
  SortAsc,
  SortDesc,
  FileText,
  Settings,
  Building2,
  Trophy,
  Smartphone
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { API_URL as API } from '../config/api';

// Import admin management components
import BlogManager from '../components/admin/BlogManager';
import SettingsManager from '../components/admin/SettingsManager';
import PartnersManager from '../components/admin/PartnersManager';
import DrawManager from '../components/admin/DrawManager';
import ManualTicketManager from '../components/admin/ManualTicketManager';
import MobilePaymentsManager from '../components/admin/MobilePaymentsManager';

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketSort, setTicketSort] = useState({ field: 'created_at', direction: 'desc' });
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionFilter, setTransactionFilter] = useState('all');
  
  // Donations state
  const [donations, setDonations] = useState([]);
  const [donationsPage, setDonationsPage] = useState(1);
  const [donationsTotal, setDonationsTotal] = useState(0);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');

  // Chart data
  const [revenueData, setRevenueData] = useState([]);

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    loadStats();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'tickets') loadTickets();
    if (activeTab === 'transactions') loadTransactions();
    if (activeTab === 'donations') loadDonations();
    if (activeTab === 'users') loadUsers();
  }, [activeTab, ticketsPage, transactionsPage, donationsPage, usersPage, transactionFilter]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers,
        withCredentials: true
      });
      setStats(response.data);
      
      // Generate mock revenue data for chart (will be replaced with real data)
      generateRevenueChartData(response.data);
    } catch (error) {
      console.error('Stats error:', error);
      if (error.response?.status === 403) {
        toast.error('Accès administrateur requis');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement des statistiques');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateRevenueChartData = (statsData) => {
    // Generate last 7 days of revenue data
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const today = new Date();
    const data = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = days[date.getDay()];
      const dateStr = `${date.getDate()}/${date.getMonth() + 1}`;
      
      // Distribute revenue across days (weighted toward recent)
      const weight = (7 - i) / 28;
      const stripeAmount = Math.round((statsData?.revenue?.stripe || 0) * weight * (0.8 + Math.random() * 0.4));
      const cryptoAmount = Math.round((statsData?.revenue?.crypto || 0) * weight * (0.8 + Math.random() * 0.4));
      
      data.push({
        name: `${dayName} ${dateStr}`,
        stripe: i === 0 ? stripeAmount : Math.round(stripeAmount * 0.8),
        crypto: i === 0 ? cryptoAmount : Math.round(cryptoAmount * 0.8),
        total: stripeAmount + cryptoAmount
      });
    }
    setRevenueData(data);
  };

  const loadTickets = async () => {
    try {
      const response = await axios.get(`${API}/admin/tickets`, {
        params: { page: ticketsPage, limit: 15, search: ticketSearch || undefined },
        headers,
        withCredentials: true
      });
      setTickets(response.data.tickets);
      setTicketsTotal(response.data.total);
    } catch (error) {
      console.error('Tickets error:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`, {
        params: { page: transactionsPage, limit: 15 },
        headers,
        withCredentials: true
      });
      let txns = response.data.transactions;
      
      // Filter by payment method if not 'all'
      if (transactionFilter !== 'all') {
        txns = txns.filter(tx => tx.payment_method === transactionFilter);
      }
      
      setTransactions(txns);
      setTransactionsTotal(response.data.total);
    } catch (error) {
      console.error('Transactions error:', error);
    }
  };

  const loadDonations = async () => {
    try {
      const response = await axios.get(`${API}/admin/donations`, {
        params: { page: donationsPage, limit: 15 },
        headers,
        withCredentials: true
      });
      setDonations(response.data.donations);
      setDonationsTotal(response.data.total);
    } catch (error) {
      console.error('Donations error:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { page: usersPage, limit: 15, search: userSearch || undefined },
        headers,
        withCredentials: true
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error('Users error:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    const headers = Object.keys(data[0]).filter(key => key !== '_id');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'object') return JSON.stringify(value);
          if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
          return value ?? '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export CSV réussi');
  };

  // Pie chart data for payment methods
  const paymentMethodData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: 'Stripe (Carte)', value: stats.revenue?.stripe || 0, color: '#3b82f6' },
      { name: 'Crypto', value: stats.revenue?.crypto || 0, color: '#f97316' }
    ].filter(item => item.value > 0);
  }, [stats]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="relative">
            <RefreshCw className="w-16 h-16 animate-spin text-primary mx-auto" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-primary/20 rounded-full animate-ping"></div>
            </div>
          </div>
          <p className="text-gray-600 mt-4 font-medium">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'tickets', label: 'Billets', icon: Ticket },
    { id: 'manual', label: 'Émission Manuelle', icon: UserCheck },
    { id: 'mobile', label: 'Paiements Mobiles', icon: Smartphone },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
    { id: 'donations', label: 'Dons', icon: Heart },
    { id: 'users', label: 'Utilisateurs', icon: Users },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'partners', label: 'Partenaires', icon: Building2 },
    { id: 'settings', label: 'Paramètres', icon: Settings },
    { id: 'draw', label: 'Tirage', icon: Trophy }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
            <p className="text-gray-600">Marathon de l'Avenir EDENLIFE 2026 - Tableau de bord</p>
          </div>
          <Button 
            onClick={loadStats} 
            variant="outline"
            className="flex items-center gap-2"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-x-auto">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center px-6 py-4 font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Revenue Card */}
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full font-medium">Total</span>
                </div>
                <p className="text-4xl font-bold">{formatCurrency(stats.revenue.total)}</p>
                <p className="text-sm opacity-80 mt-2">Revenus totaux</p>
                <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    <span>{formatCurrency(stats.revenue.stripe)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Wallet className="w-4 h-4" />
                    <span>{formatCurrency(stats.revenue.crypto)}</span>
                  </div>
                </div>
              </div>

              {/* Tickets Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Ticket className="w-6 h-6" />
                  </div>
                  <div className="flex items-center gap-1 text-xs bg-white/20 px-3 py-1 rounded-full">
                    <ArrowUpRight className="w-3 h-3" />
                    +{stats.tickets.recent_7_days}
                  </div>
                </div>
                <p className="text-4xl font-bold">{stats.tickets.total}</p>
                <p className="text-sm opacity-80 mt-2">Billets vendus</p>
                <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                  <div className="flex justify-between">
                    <span>Actifs</span>
                    <span className="font-bold">{stats.tickets.active}</span>
                  </div>
                </div>
              </div>

              {/* Donations Card */}
              <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Heart className="w-6 h-6" />
                  </div>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full font-medium">Dons</span>
                </div>
                <p className="text-4xl font-bold">{formatCurrency(stats.donations.total)}</p>
                <p className="text-sm opacity-80 mt-2">Total des dons</p>
                <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                  <div className="flex justify-between">
                    <span>Donateurs</span>
                    <span className="font-bold">{stats.donations.count}</span>
                  </div>
                </div>
              </div>

              {/* Users Card */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="flex items-center gap-1 text-xs bg-white/20 px-3 py-1 rounded-full">
                    <ArrowUpRight className="w-3 h-3" />
                    +{stats.users.recent_7_days}
                  </div>
                </div>
                <p className="text-4xl font-bold">{stats.users.total}</p>
                <p className="text-sm opacity-80 mt-2">Utilisateurs</p>
                <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                  <div className="flex justify-between">
                    <span>Affiliés</span>
                    <span className="font-bold">{stats.affiliates.total}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Area Chart */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Revenus - 7 derniers jours
                  </h3>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorStripe" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorCrypto" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="stripe"
                        name="Carte"
                        stroke="#3b82f6"
                        fillOpacity={1}
                        fill="url(#colorStripe)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="crypto"
                        name="Crypto"
                        stroke="#f97316"
                        fillOpacity={1}
                        fill="url(#colorCrypto)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Payment Methods Pie Chart */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-6">
                  <PieChartIcon className="w-5 h-5 text-primary" />
                  Méthodes de paiement
                </h3>
                <div className="h-56">
                  {paymentMethodData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={paymentMethodData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {paymentMethodData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">
                      <p>Aucune donnée disponible</p>
                    </div>
                  )}
                </div>
                {/* Summary below pie chart */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-sm">Carte</span>
                    </div>
                    <span className="font-bold text-blue-600">{formatCurrency(stats.revenue.stripe)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm">Crypto</span>
                    </div>
                    <span className="font-bold text-orange-600">{formatCurrency(stats.revenue.crypto)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Résumé des ventes
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <span className="text-gray-600">Transactions totales</span>
                    <span className="font-bold text-lg">{stats.revenue.purchases_count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <span className="text-gray-600">Prix moyen / transaction</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(stats.revenue.purchases_count > 0 ? stats.revenue.total / stats.revenue.purchases_count : 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <span className="text-gray-600">Commissions affiliés</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(stats.affiliates.total_commissions)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-emerald-700 font-medium">Objectif de campagne</span>
                    <div className="text-right">
                      <span className="font-bold text-lg text-emerald-600">
                        {((stats.revenue.total / 500000) * 100).toFixed(1)}%
                      </span>
                      <p className="text-xs text-emerald-600">{formatCurrency(stats.revenue.total)} / $500,000</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Programme d'affiliation
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-gray-600">Affiliés actifs</span>
                    <span className="font-bold text-lg text-purple-600">{stats.affiliates.total}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Ventes via affiliés</span>
                    <span className="font-bold text-lg">{formatCurrency(stats.affiliates.total_commissions * 10)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Commissions versées</span>
                    <span className="font-bold text-lg">{formatCurrency(stats.affiliates.total_commissions)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-700 font-medium">Taux de commission</span>
                    <span className="font-bold text-lg text-yellow-600">10%</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm"
          >
            <div className="p-6 border-b">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl font-bold">Liste des billets ({ticketsTotal})</h2>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher un billet..."
                      value={ticketSearch}
                      onChange={(e) => setTicketSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && loadTickets()}
                      className="pl-10"
                      data-testid="ticket-search-input"
                    />
                  </div>
                  <Button onClick={loadTickets} variant="outline" size="icon" data-testid="refresh-tickets-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => exportToCSV(tickets, 'billets')} 
                    variant="outline" 
                    size="icon"
                    title="Exporter CSV"
                    data-testid="export-tickets-btn"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        Aucun billet trouvé
                      </td>
                    </tr>
                  ) : (
                    tickets.map((ticket) => (
                      <tr key={ticket.ticket_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-mono font-bold text-primary">{ticket.ticket_number}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{ticket.user?.name || '-'}</p>
                            <p className="text-sm text-gray-500">{ticket.user?.email || '-'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-medium">{formatCurrency(ticket.price)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            ticket.status === 'active' ? 'bg-green-100 text-green-700' :
                            ticket.status === 'used' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {ticket.status === 'active' ? 'Actif' : ticket.status === 'used' ? 'Utilisé' : ticket.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(ticket.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="p-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {ticketsPage} sur {Math.ceil(ticketsTotal / 15) || 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTicketsPage(p => Math.max(1, p - 1))}
                  disabled={ticketsPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTicketsPage(p => p + 1)}
                  disabled={ticketsPage >= Math.ceil(ticketsTotal / 15)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm"
          >
            <div className="p-6 border-b">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl font-bold">Transactions ({transactionsTotal})</h2>
                <div className="flex items-center gap-2">
                  {/* Filter buttons */}
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setTransactionFilter('all')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        transactionFilter === 'all' ? 'bg-white shadow text-primary font-medium' : 'text-gray-600'
                      }`}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => setTransactionFilter('stripe')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                        transactionFilter === 'stripe' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      <CreditCard className="w-3 h-3" /> Carte
                    </button>
                    <button
                      onClick={() => setTransactionFilter('crypto')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                        transactionFilter === 'crypto' ? 'bg-white shadow text-orange-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      <Wallet className="w-3 h-3" /> Crypto
                    </button>
                  </div>
                  <Button onClick={loadTransactions} variant="outline" size="icon">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => exportToCSV(transactions, 'transactions')} 
                    variant="outline" 
                    size="icon"
                    title="Exporter CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Méthode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        Aucune transaction trouvée
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx, index) => (
                      <tr key={tx.purchase_id || tx.payment_id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm text-gray-600">{tx.purchase_id || tx.order_id || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{tx.user?.name || tx.email || '-'}</p>
                            <p className="text-sm text-gray-500">{tx.user?.email || '-'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-bold text-green-600">{formatCurrency(tx.total_amount)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            tx.payment_method === 'crypto' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {tx.payment_method === 'crypto' ? (
                              <><Wallet className="w-3 h-3 mr-1" /> Crypto</>
                            ) : (
                              <><CreditCard className="w-3 h-3 mr-1" /> Carte</>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            tx.payment_status === 'completed' || tx.status === 'finished' ? 'bg-green-100 text-green-700' :
                            tx.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {tx.payment_status === 'completed' ? 'Complété' : 
                             tx.payment_status === 'pending' ? 'En attente' : 
                             tx.payment_status || tx.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(tx.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="p-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {transactionsPage} sur {Math.ceil(transactionsTotal / 15) || 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTransactionsPage(p => Math.max(1, p - 1))}
                  disabled={transactionsPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTransactionsPage(p => p + 1)}
                  disabled={transactionsPage >= Math.ceil(transactionsTotal / 15)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Donations Tab */}
        {activeTab === 'donations' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm"
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Dons ({donationsTotal})</h2>
                <div className="flex gap-2">
                  <Button onClick={loadDonations} variant="outline" size="icon">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => exportToCSV(donations, 'dons')} 
                    variant="outline" 
                    size="icon"
                    title="Exporter CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Donateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {donations.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        Aucun don trouvé
                      </td>
                    </tr>
                  ) : (
                    donations.map((donation, index) => (
                      <tr key={donation.donation_id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              donation.is_anonymous ? 'bg-gray-100' : 'bg-pink-100'
                            }`}>
                              {donation.is_anonymous ? '🎭' : (
                                <span className="font-bold text-pink-600">
                                  {donation.donor_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {donation.is_anonymous ? 'Anonyme' : donation.donor_name || '-'}
                              </p>
                              <p className="text-sm text-gray-500">{donation.donor_email || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-pink-600 text-lg">{formatCurrency(donation.amount)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-600 max-w-xs truncate" title={donation.message}>
                            {donation.message || '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            donation.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {donation.status === 'completed' ? 'Complété' : 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(donation.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="p-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {donationsPage} sur {Math.ceil(donationsTotal / 15) || 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDonationsPage(p => Math.max(1, p - 1))}
                  disabled={donationsPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDonationsPage(p => p + 1)}
                  disabled={donationsPage >= Math.ceil(donationsTotal / 15)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm"
          >
            <div className="p-6 border-b">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl font-bold">Utilisateurs ({usersTotal})</h2>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher un utilisateur..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && loadUsers()}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Button onClick={loadUsers} variant="outline" size="icon">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => exportToCSV(users, 'utilisateurs')} 
                    variant="outline" 
                    size="icon"
                    title="Exporter CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billets</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inscription</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        Aucun utilisateur trouvé
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                              u.role === 'admin' ? 'bg-purple-100' : 'bg-primary/10'
                            }`}>
                              <span className={`font-bold ${u.role === 'admin' ? 'text-purple-600' : 'text-primary'}`}>
                                {u.name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{u.name || '-'}</p>
                              <p className="text-sm text-gray-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {u.role === 'admin' ? (
                              <><Award className="w-3 h-3 mr-1" /> Admin</>
                            ) : (
                              <><UserCheck className="w-3 h-3 mr-1" /> Utilisateur</>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Ticket className="w-4 h-4 text-primary" />
                            <span className="font-bold text-primary">{u.ticket_count || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(u.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="p-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {usersPage} sur {Math.ceil(usersTotal / 15) || 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                  disabled={usersPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUsersPage(p => p + 1)}
                  disabled={usersPage >= Math.ceil(usersTotal / 15)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && <BlogManager />}

        {/* Manual Ticket Issuance Tab */}
        {activeTab === 'manual' && <ManualTicketManager />}

        {/* Mobile Payments Tab */}
        {activeTab === 'mobile' && <MobilePaymentsManager />}

        {/* Partners Tab */}
        {activeTab === 'partners' && <PartnersManager />}

        {/* Settings Tab */}
        {activeTab === 'settings' && <SettingsManager />}

        {/* Draw Tab */}
        {activeTab === 'draw' && <DrawManager />}
      </div>
    </div>
  );
};

export default Admin;
